"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";

type Step = "email" | "code";
type Status = "idle" | "submitting" | "error";

const RESEND_COOLDOWN_SECONDS = 30;

export default function LoginPage() {
  const router = useRouter();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    if (step === "code") codeInputRef.current?.focus();
  }, [step]);

  async function sendCode() {
    setStatus("submitting");
    setErrorMessage("");
    try {
      const res = await fetch("/api/auth/request-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.status === 429) {
        const data = await res.json().catch(() => null);
        setCooldown(data?.retryAfterSeconds ?? RESEND_COOLDOWN_SECONDS);
        setStatus("idle");
        setStep("code");
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || "request failed");
      }
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setStatus("idle");
      setStep("code");
    } catch (err) {
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  }

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    await sendCode();
  }

  async function handleCodeSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");
    try {
      const res = await fetch("/api/auth/verify-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        setStatus("error");
        setErrorMessage(data?.error || "That code didn't work. Check it and try again.");
        return;
      }
      router.replace("/");
      router.refresh();
    } catch {
      setStatus("error");
      setErrorMessage("Something went wrong. Please try again.");
    }
  }

  function handleChangeEmail() {
    setStep("email");
    setCode("");
    setStatus("idle");
    setErrorMessage("");
    setCooldown(0);
  }

  if (step === "code") {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-title">Enter your code</div>
          <div className="auth-desc">
            We sent a 6-digit code to <strong>{email}</strong>. Enter it
            below to sign in.
          </div>
          <form onSubmit={handleCodeSubmit}>
            <input
              ref={codeInputRef}
              className="auth-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              disabled={status === "submitting"}
            />
            <button
              className="btn btn-primary"
              type="submit"
              disabled={status === "submitting" || code.length !== 6}
              style={{ width: "100%", justifyContent: "center" }}
            >
              {status === "submitting" ? "Verifying…" : "Sign in"}
            </button>
          </form>
          {errorMessage && (
            <div className="auth-message error">{errorMessage}</div>
          )}
          <div className="auth-desc" style={{ marginTop: 16 }}>
            <button
              type="button"
              className="auth-link-btn"
              onClick={sendCode}
              disabled={cooldown > 0 || status === "submitting"}
            >
              {cooldown > 0
                ? `Send another code in ${cooldown}s`
                : "Send another code"}
            </button>
          </div>
          <div className="auth-desc">
            <button type="button" className="auth-link-btn" onClick={handleChangeEmail}>
              Use a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-title">Seattle Opera Board Portal</div>
        <div className="auth-desc">
          Enter your email and we&apos;ll send you a one-time code to sign
          in — no password needed.
        </div>
        <form onSubmit={handleEmailSubmit}>
          <input
            className="auth-input"
            type="email"
            placeholder="you@example.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === "submitting"}
          />
          <button
            className="btn btn-primary"
            type="submit"
            disabled={status === "submitting"}
            style={{ width: "100%", justifyContent: "center" }}
          >
            {status === "submitting" ? "Sending…" : "Send sign-in code"}
          </button>
        </form>
        {errorMessage && (
          <div className="auth-message error">{errorMessage}</div>
        )}
      </div>
    </div>
  );
}
